<template>
<Form
	ref="form"
	class="m-0 p-0 g-0"
	@submit="onSubmit"
	@invalid-submit="console.log"
	:validation-schema="schema"
	:initial-values="formValues"
	v-slot="{ values, errors, meta, isSubmitting, }"
>
<fieldset :disabled="!isFormEnabled">
	<div style="display: none;">
		{{ formMeta = meta }}
		{{ isSubmittingForm = isSubmitting }}
	</div>

	<div class="form-row row m-0 p-0 g-3">
		<div class="col-12 mt-1">
			<label class="fw-bold">
				Первинні налаштування
			</label>
		</div>

		<div class="col-12 col-xl-6 mt-1">
			<label>{{ ElectricMeter.settingNames[ElectricMeter.passportKeys.type] }}</label>
			<Field
				:name="ElectricMeter.passportKeys.type"
				as="select"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors[ElectricMeter.passportKeys.type] }"
			>
				<option value="" disabled>Оберіть тип лічильника</option>
				<option v-for="version in Object.values(ElectricMeter.versions)" :key="version" :value="version">
					{{ ElectricMeter.versionNames[version] }}
				</option>
			</Field>
			<div class="invalid-feedback">
				{{ errors[ElectricMeter.passportKeys.type] }}
			</div>
			<span
				v-if="
					[ElectricMeter.versions.nikDlms, ElectricMeter.versions.gama100G1b]
						.includes(values[ElectricMeter.passportKeys.type])
				"
				class="form-text fst-italic text-break"
			>
				<b class="fw-bold">Рекомендовані налаштування порта модема:</b><br/>
				• швидкість - 9600,<br/>
				• біти даних - 8,<br/>
				• стоп біти - 1,<br/>
				• парність - вимкнено,<br/>
				• конвертування ModBusTCP {{ '<->' }} ModBusRTU - вимкнено.
			</span>
			<span
				v-if="values[ElectricMeter.passportKeys.type] == ElectricMeter.versions.gama100G1m"
				class="form-text fst-italic text-break"
			>
				<b class="fw-bold">Рекомендовані налаштування порта модема:</b><br/>
				• швидкість - 9600,<br/>
				• біти даних - 7,<br/>
				• стоп біти - 1,<br/>
				• парність - парність,<br/>
				• конвертування ModBusTCP {{ '<->' }} ModBusRTU - вимкнено.
			</span>
		</div>

		<div class="col-12 col-xl-6">
			<div class="bg-body border-radius px-3 py-2">
				<ol class="list-group list-group-flush">
					<span class="form-text fst-italic fw-bold">Типи лічильника</span>
					<li class="list-group-item">
						<span class="form-text fst-italic fw-bold">Імпульсний вхід: </span>
						<span class="form-text fst-italic text-break">
							використовується лічильник імпульсів сервісного модуля. Також треба налаштувати "Коеффіцієнт перетворення лічильника єлектроенергії" сервісного модуля.
						</span>
					</li>
					<li class="list-group-item">
						<span class="form-text fst-italic fw-bold">NIK/DLMS: </span>
						<span class="form-text fst-italic text-break">
							окремо розміщений лічильник NIK що опитується за відповідним протоколом.
							Будь ласка, налаштуйте порт модема, якщо лічильник під'єднано через модем.
						</span>
					</li>
					<li class="list-group-item">
						<span class="form-text fst-italic fw-bold">Gama100 G1*: </span>
						<span class="form-text fst-italic text-break">
							окремо розміщений лічильник Gama що опитується за відповідним протоколом.
							Будь ласка, налаштуйте порт модема, якщо лічильник під'єднано через модем.
						</span>
					</li>
				</ol>
			</div>
		</div>
	</div>
		
	<!-- If meter type is separate module -->
	<div
		v-if="
			[ElectricMeter.versions.nikDlms, ElectricMeter.versions.gama100G1m, ElectricMeter.versions.gama100G1b]
				.includes(values[ElectricMeter.passportKeys.type])
		"
		class="form-row row m-0 p-0 g-3"
	>
		<div class="col-12 col-xl-6">
			<div class="row">
				<div class="col-10">
					<label>{{ ElectricMeter.settingNames[ElectricMeter.passportKeys.surveyPeriod] }}</label>
					<Field
						:name="ElectricMeter.passportKeys.surveyPeriod"
						type="number"
						class="form-control form-control-sm no-arrow"
						:class="{
							'is-invalid': errors[ElectricMeter.passportKeys.surveyPeriod]
						}"
					/>
					<div class="invalid-feedback">
						{{ errors[ElectricMeter.passportKeys.surveyPeriod] }}
					</div>
				</div>

				<div class="col-2">
					<label>&nbsp;</label>
					<Field
						:name="ElectricMeter.passportKeys.surveyPeriodMultiplier"
						as="select"
						class="form-control form-control-sm"
					>
						<option :value="$options.SECONDS_IN_MINUTE">хв</option>
						<option :value="$options.SECONDS_IN_HOUR">год</option>
						<option :value="$options.SECONDS_IN_DAY">днів</option>
						<option :value="$options.SECONDS_IN_WEEK">тиж</option>
						<option :value="$options.SECONDS_IN_MONTH">міс</option>
						<option :value="$options.SECONDS_IN_YEAR">р</option>
					</Field>
				</div>

				<div
					v-if="values[ElectricMeter.passportKeys.surveyPeriodMultiplier] == $options.SECONDS_IN_DAY
							&& values[Station.passportKeys.electricMeter.surveyPeriod] > 1
							|| values[ElectricMeter.passportKeys.surveyPeriodMultiplier] > $options.SECONDS_IN_DAY"
					class="col-12"
				>
					<label class="mt-2"> {{ ElectricMeter.settingNames[ElectricMeter.passportKeys.surveyStartTime] }}</label>
					<Field
						:name="ElectricMeter.passportKeys.surveyStartTime"
						type="time"
						step="1"
						class="form-control form-control-sm no-arrow"
						:class="{ 'is-invalid': errors[ElectricMeter.passportKeys.surveyStartTime] }"
					/>
					<div class="invalid-feedback">
						{{errors[ElectricMeter.passportKeys.surveyStartTime]}}
					</div>
				</div>
			</div>
		</div>

		<div class="col-12 col-xl-6">
			<div class="bg-body border-radius px-3 py-2">
				<span class="form-text fst-italic">
					Графік опитування матиме вигляд:<br>
					<span v-for="(point, index) in buildCalendarForElectricMeter(values).slice(0, $options.SURVEY_CALENDAR_SHOW_COUNT - 1)"
						:key="index"
					>
						{{ point }}&nbsp;
					</span>
					<span v-if="buildCalendarForElectricMeter(values).length > $options.SURVEY_CALENDAR_SHOW_COUNT"
					>
						...&nbsp;{{ buildCalendarForElectricMeter(values)[buildCalendarForElectricMeter(values).length - 1] }}
						&nbsp;&nbsp;&nbsp;(всього&nbsp;{{ buildCalendarForElectricMeter(values).length }})
					</span>
				</span>
			</div>
		</div>
	</div>

	<!-- If meter type is impulse input -->
	<div class="row form-row m-0 p-0 g-3" v-if="values[ElectricMeter.passportKeys.type] == ElectricMeter.versions.impulseInput">
		<span class="form-text mt-2">
			Налаштуйте "<b>Лічильник імпульсів електролічильника</b>" сервісного модуля.
		</span>
	</div>
	<!-- If meter type is nik dlms -->
	<div
		class="row form-row m-0 p-0 g-3"
		v-if="
			[ElectricMeter.versions.nikDlms, ElectricMeter.versions.gama100G1m, ElectricMeter.versions.gama100G1b]
				.includes(values[ElectricMeter.passportKeys.type])
		"
	>
		<span class="form-text mt-2">
			Для данного типу <b>обов'язково заповніть всі поля</b> налаштувань.
		</span>
	</div>

	<!-- If meter type is separate module -->
	<div
		v-if="
			[ElectricMeter.versions.nikDlms, ElectricMeter.versions.gama100G1m, ElectricMeter.versions.gama100G1b]
				.includes(values[ElectricMeter.passportKeys.type])
		"
		class="form-row row m-0 p-0 g-3"
	>
		<div class="col-12 col-xl-6">
			<label>{{ ElectricMeter.settingNames[ElectricMeter.passportKeys.manufacturerId] }}</label>
			<Field
				:name="ElectricMeter.passportKeys.manufacturerId"
				type="text"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors[ElectricMeter.passportKeys.manufacturerId] }"
			/>
			<div class="invalid-feedback">
				{{ errors[ElectricMeter.passportKeys.manufacturerId] }}
			</div>
		</div>

		<div class="col-12 col-xl-6 d-none d-xl-block">
			&nbsp;
		</div>
	</div>

	<!-- If meter type is separate module -->
	<div
		v-if="
			[ElectricMeter.versions.nikDlms, ElectricMeter.versions.gama100G1m, ElectricMeter.versions.gama100G1b]
				.includes(values[ElectricMeter.passportKeys.type])
		"
		class="form-row row m-0 p-0 g-3"
	>
		<div class="col-12 col-xl-6">
			<label>{{ ElectricMeter.settingNames[ElectricMeter.passportKeys.ip] }}</label>
			<Field
				:name="ElectricMeter.passportKeys.ip"
				type="text"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors[ElectricMeter.passportKeys.ip] }"
			/>
			<div class="invalid-feedback">
				{{ errors[ElectricMeter.passportKeys.ip] }}
			</div>
		</div>

		<div class="col-12 col-xl-6 d-none d-xl-block">
			&nbsp;
		</div>
	</div>

	<!-- If meter type is separate module -->
	<div
		v-if="
			[ElectricMeter.versions.nikDlms, ElectricMeter.versions.gama100G1m, ElectricMeter.versions.gama100G1b]
				.includes(values[ElectricMeter.passportKeys.type])
		"
		class="form-row row m-0 p-0 g-3"
	>
		<div class="col-12 col-xl-6">
			<label>{{ ElectricMeter.settingNames[ElectricMeter.passportKeys.port] }}</label>
			<Field
				:name="ElectricMeter.passportKeys.port"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[ElectricMeter.passportKeys.port] }"
			/>
			<div class="invalid-feedback">
				{{ errors[ElectricMeter.passportKeys.port] }}
			</div>
		</div>

		<div class="col-12 col-xl-6 d-none d-xl-block">
			&nbsp;
		</div>
	</div>

	<!-- If meter type is nik dlms -->
	<div
		v-if="values[ElectricMeter.passportKeys.type] == ElectricMeter.versions.nikDlms"
		class="form-row row m-0 p-0 g-3"
	>
		<div class="col-12 col-xl-6">
			<label>{{ ElectricMeter.settingNames[ElectricMeter.passportKeys.user] }}</label>
			<Field
				:name="ElectricMeter.passportKeys.user"
				as="select"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors[ElectricMeter.passportKeys.user] }"
			>
				<option value="" disabled>Оберіть тип користувача</option>
				<option key="user" value="user">
					Користувач
				</option>
				<option key="operator" value="operator">
					Оператор
				</option>
				<option key="admin" value="admin">
					Адміністратор
				</option>
			</Field>
			<div class="invalid-feedback">
				{{ errors[ElectricMeter.passportKeys.user] }}
			</div>
		</div>

		<div class="col-12 col-xl-6 d-none d-xl-block">
			&nbsp;
		</div>
	</div>

	<!-- If meter type is nik dlms -->
	<div
		v-if="values[ElectricMeter.passportKeys.type] == ElectricMeter.versions.nikDlms"
		class="form-row row m-0 p-0 g-3"
	>
		<div class="col-12 col-xl-6">
			<label>{{ ElectricMeter.settingNames[ElectricMeter.passportKeys.password] }}</label>
			<Field
				:name="ElectricMeter.passportKeys.password"
				type="text"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[ElectricMeter.passportKeys.password] }"
			/>
			<div class="invalid-feedback">
				{{ errors[ElectricMeter.passportKeys.password] }}
			</div>
		</div>

		<div class="col-12 col-xl-6 d-none d-xl-block">
			&nbsp;
		</div>
	</div>

	<div class="row form-row m-0 mt-2 p-0">
		<p :class="feedbackPromtClass">
			{{ feedbackPromt }}&nbsp;
		</p>
	</div>

	<div class="row form-row align-self-center m-0 p-0 mt-1 mb-3">
		<div class="col">
			<button
				ref="submitButtonRef"
				type="submit"
				class="btn btn-primary btn-sm"
				:disabled="!meta.valid || !meta.dirty || isSubmitting"
			>
				Записати
			</button>
		</div>
	</div>
</fieldset>
</Form>
</template>




<script>
import { useStationsStore } from '@/stores/StationsStore.js'
import { useAccontStore } from '@/stores/AccountStore.js'

// Validation
// https://jasonwatmore.com/post/2020/10/01/vue-3-veevalidate-form-validation-example
import { Form, Field } from 'vee-validate'
// https://github.com/jquense/yup/tree/a58f02d2f6164c46a9757a818eebac582f7a441c#boolean
import * as Yup from 'yup';

import { Station } from '@/models/station.js'
import { ElectricMeter } from '@/models/electricMeter.js';
import {
	parseTimeDate,
	parseApiTimeDate,
	convertToApiTimeDate,
} from '@/api/api.js'
import {
	capitalizeFirstLetter,
	dateTimeToString,
	buildCalendar,
} from '@/helpers.js'


export default {
	requiredPromt: 'Обов\'язкове поле',
	overMinLenPromt: 'Довжина меньше мінімальної',
	overMaxLenPromt: 'Довжина більше максимальної',
	overMinPromt: 'Значення меньше мінімального',
	overMaxPromt: 'Значення більше максимального',
	onlyIntegersPromt: 'Має бути цілим числом',
	onlyPositivePromt: 'Має бути натуральним числом',
	wrongInputPromt: 'Не відповідає вимогам',

	SECONDS_IN_YEAR: 31536000,
	SECONDS_IN_MONTH: 2592000,
	SECONDS_IN_WEEK: 604800,
	SECONDS_IN_DAY: 86400,
	SECONDS_IN_HOUR: 3600,
	SECONDS_IN_MINUTE: 60,

	SURVEY_CALENDAR_SHOW_COUNT: 16,

	components: {
		Form,
        Field,
	},

	props: {
		stationId: {
			type: String,
			require: true,
		},
	},

	emits: {
		saveFunction: (f) => typeof f === 'function',
		saveFunctionActive: (f) => typeof f === 'boolean',
	},

	setup() {
		const stationsStore = useStationsStore()
		const accountStore = useAccontStore()

		return {
			stationsStore,
			accountStore,
			Station,
			ElectricMeter,
			capitalizeFirstLetter,
			parseTimeDate,
			dateTimeToString,
		}
	},

	data() {
		const schema = Yup.object().shape({
			[ElectricMeter.passportKeys.type]: Yup.string()
				.typeError(this.$options.wrongInputPromt)
				.min(3, this.$options.overMinLenPromt)
				.max(64, this.$options.overMaxLenPromt),
			[ElectricMeter.passportKeys.ip]: Yup.string()
				.when('type', {
					is: ElectricMeter.versions.nikDlms, // type of meter
					then: (schema) => schema.required(this.$options.requiredPromt),
				})
				.typeError(this.$options.wrongInputPromt)
				.min(7, this.$options.overMinLenPromt)
				.max(15, this.$options.overMaxLenPromt)
				.matches(/(\b25[0-5]|\b2[0-4][0-9]|\b[01]?[0-9][0-9]?)(\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}/,
					'Може містити тільки IPv4 адресу'),
			[ElectricMeter.passportKeys.port]: Yup.number()
				.when('type', {
						is: ElectricMeter.versions.nikDlms, // type of meter
						then: (schema) => schema.required(this.$options.requiredPromt),
					})
				.typeError(this.$options.wrongInputPromt)
				.integer(this.$options.onlyIntegersPromt)
				.min(1, this.$options.overMinPromt)
				.max(65535, this.$options.overMaxPromt),
			[ElectricMeter.passportKeys.user]: Yup.string()
				.when('type', {
						is: ElectricMeter.versions.nikDlms, // type of meter
						then: (schema) => schema.required(this.$options.requiredPromt),
					})
				.typeError(this.$options.wrongInputPromt)
				.min(1, this.$options.overMinLenPromt)
				.max(16, this.$options.overMaxLenPromt),
			[ElectricMeter.passportKeys.password]: Yup.string()
				.when('type', {
						is: ElectricMeter.versions.nikDlms, // type of meter
						then: (schema) => schema.required(this.$options.requiredPromt),
					})
				.typeError(this.$options.wrongInputPromt)
				.min(1, this.$options.overMinLenPromt)
				.max(16, this.$options.overMaxLenPromt),
			[ElectricMeter.passportKeys.manufacturerId]: Yup.string()
				.when('type', {
					is: ElectricMeter.versions.nikDlms, // type of meter
					then: (schema) => schema.required(this.$options.requiredPromt),
				})
				.typeError(this.$options.wrongInputPromt)
				.min(1, this.$options.overMinLenPromt)
				.max(64, this.$options.overMaxLenPromt),
			[ElectricMeter.passportKeys.surveyPeriod]: Yup.number()
				.min(1, this.$options.overMinPromt)
				.max(60, this.$options.overMaxPromt)
				.integer(this.$options.onlyIntegersPromt)
				.positive(this.$options.onlyPositivePromt)
				.typeError(this.$options.wrongInputPromt)
				.when(ElectricMeter.passportKeys.surveyPeriodMultiplier, (multiplier, schema) => {
					// If no multiplier
					if (!multiplier) {
						return schema
							.min(60, this.$options.overMinPromt)
							.max(31536000, this.$options.overMaxPromt)
					}

					const minValue = 60 / multiplier;
					const maxValue = 31536000 / multiplier;

					return schema
						.min(minValue, this.$options.overMinPromt)
						.max(maxValue, this.$options.overMaxPromt)
				}),
			[ElectricMeter.passportKeys.surveyPeriodMultiplier]: Yup.number()
				.integer(this.$options.onlyIntegersPromt)
				.positive(this.$options.onlyPositivePromt),
			[ElectricMeter.passportKeys.surveyStartTime]: Yup.string()
				.typeError(this.$options.wrongInputPromt),
		})

		return {
			// Form
			schema,
			formMeta: {},
			isSubmittingForm: false,

			// Feedback promt
			feedbackPromtClass: '',
			feedbackPromt: '',
		}
	},

	watch: {
		formMeta(newMeta) {
			if (newMeta) {
				this.$emit('saveFunctionActive', newMeta.valid && newMeta.dirty && !this.isSubmittingForm);
				// console.log('meta change', newMeta, !newMeta.valid || !newMeta.dirty || this.isSubmittingForm)
			}
		},
	},

	created() {},

	mounted() {
		// Emit function
		this.$emit('saveFunction', this.submitButtonClick)
	},

	methods: {
		onSubmit(values) {
			// Convert time fields to datetime for api compatibility
			//
			// electric meter survey_start_time
			let time = values[Station.passportKeys.electricMeter.surveyStartTime]
			if (time) {
				let date = new Date()
				date.setHours(time?.slice(0,2), time?.slice(3,5), time?.slice(6,8), 0)
				values[Station.passportKeys.electricMeter.surveyStartTime] = convertToApiTimeDate(date)
			} else {
				let date = new Date()
				date.setHours(0, 0, 0, 0)
				values[Station.passportKeys.electricMeter.surveyStartTime] = convertToApiTimeDate(date)
			}

			// Translate survey period
			// For electric meter
			values[Station.passportKeys.electricMeter.surveyPeriod] = Math.floor(
				values[Station.passportKeys.electricMeter.surveyPeriod]
				* values[ElectricMeter.passportKeys.surveyPeriodMultiplier]
			)
			delete values[ElectricMeter.passportKeys.surveyPeriodMultiplier]

			// Move values under electric meter key
			values = { [ElectricMeter.passportKeys.self]: values }

			// Add curent user as passport updator
			values[Station.passportKeys.updateBy] = this.accountStore.userId

			this.stationsStore.updatePasport(
				(result) => {
					if (result) {
						this.printHint('Паспорт успішно оновлено.')
						// Update passport
						this.stationsStore.readPassport(
							() => {},
							this.stationId
						)
					} else {
						this.printErr('Сервер відхилив запит.')
					}
				},
				this.stationId,
				values,
			)
		},

		submitButtonClick() {
			this.$refs.submitButtonRef.click()
		},

		buildCalendarForElectricMeter(formValues) {
			if (!formValues[Station.passportKeys.electricMeter.surveyPeriod]) {
				return buildCalendar(undefined, undefined)
			}

			const period = formValues[Station.passportKeys.electricMeter.surveyPeriod]
				* formValues[ElectricMeter.passportKeys.surveyPeriodMultiplier]
			const startTimeString = formValues[Station.passportKeys.electricMeter.surveyStartTime]

			return buildCalendar(period, startTimeString)
		},

		printHint (msg) {
			this.feedbackPromtClass = "text-success"
			this.feedbackPromt = msg
		},

		printErr (msg) {
			this.feedbackPromtClass = "text-danger"
			this.feedbackPromt = msg
		},
	},

	computed: {
		station() { return this.stationsStore.station(this.stationId) },

		isFormEnabled() { return this.accountStore.permissions.includes('manage_stations') },

		formValues() {
			const electricMeterUnitPassport = this.station?.passport.get(ElectricMeter.passportKeys.self)
			
			// Translate datetime to time fields
			//
			// electric meter survey_start_time
			let time = electricMeterUnitPassport[Station.passportKeys.electricMeter.surveyStartTime]
			if (time) {
				time = parseApiTimeDate(time)
				electricMeterUnitPassport[Station.passportKeys.electricMeter.surveyStartTime] = 
					`${String(time.getHours()).padStart(2, '0')}:${String(time.getMinutes()).padStart(2, '0')}:${String(time.getSeconds()).padStart(2, '0')}`
			}

			// Calculate survey time multiplier
			// For electric meter
			const electricMeterSurveyPeriod = electricMeterUnitPassport[Station.passportKeys.electricMeter.surveyPeriod]
			if (electricMeterSurveyPeriod >= this.$options.SECONDS_IN_YEAR) {
				electricMeterUnitPassport.surveyPeriodMultiplier = this.$options.SECONDS_IN_YEAR
			} else if (electricMeterSurveyPeriod >= this.$options.SECONDS_IN_MONTH) {
				electricMeterUnitPassport.surveyPeriodMultiplier = this.$options.SECONDS_IN_MONTH
			} else if (electricMeterSurveyPeriod >= this.$options.SECONDS_IN_WEEK) {
				electricMeterUnitPassport.surveyPeriodMultiplier = this.$options.SECONDS_IN_WEEK
			} else if (electricMeterSurveyPeriod >= this.$options.SECONDS_IN_DAY) {
				electricMeterUnitPassport.surveyPeriodMultiplier = this.$options.SECONDS_IN_DAY
			} else if (electricMeterSurveyPeriod >= this.$options.SECONDS_IN_HOUR) {
				electricMeterUnitPassport.surveyPeriodMultiplier = this.$options.SECONDS_IN_HOUR
			} else if (electricMeterSurveyPeriod >= this.$options.SECONDS_IN_MINUTE) {
				electricMeterUnitPassport.surveyPeriodMultiplier = this.$options.SECONDS_IN_MINUTE
			} else {
				electricMeterUnitPassport.surveyPeriodMultiplier = 1
			}
			electricMeterUnitPassport[Station.passportKeys.electricMeter.surveyPeriod] = Math.floor(
				electricMeterSurveyPeriod / electricMeterUnitPassport.surveyPeriodMultiplier
			)

			return electricMeterUnitPassport
		},
	},
}
</script>




<style scoped>
/* Remove arrows from input Chrome, Safari, Edge, Opera */
input.no-arrow::-webkit-outer-spin-button,
input.no-arrow::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

/* Remove arrows from input Firefox */
input[type=number].no-arrow {
	appearance: textfield;
	-moz-appearance: textfield;
}

/* Style for input if it is not match register value */
.not-match-register {
	border-color: rgb(11, 94, 215);
	color: rgb(11, 94, 215);
}
</style>